import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import InfoIcon from "@mui/icons-material/Info";
import { Input, InputSuffix, NumericTextBox, NumericTextBoxChangeEvent, TextBox, TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { Button } from '@progress/kendo-react-buttons';
import { Icon } from '@progress/kendo-react-common';
import styles from "./_vehicle-editor-dialog.module.scss";
import { capitalizeFirstLetterOfEachWord, numberWithCommas, userHasPermission } from '../../../utils/utils';
import { ComboBox } from '@progress/kendo-react-dropdowns';
import { Constants, permissions } from '../../../utils/constants';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../../../store/rootReducer';
import { IAmsAcvPermissions, SET_AMS_ACV_PERMISSIONS } from '../../../store/application/types';
import { fetchData } from '../../../Data/fetch';
import {hasValue, LookupItem, PermissionSet, Vehicle} from '@amsanalytics/react-shared-library';
import { ILoggedInEmployee } from '../../../Interfaces/Admin/ILoggedInEmployee';
import classNames from 'classnames';
import {
  AmsLookupForComboBoxDisplay,
  getAmsLocationsOfSaleToDisplay, getAmsLookupsToDisplay
} from "../../Views/OrderEditor/AmsLoanPostingDialog/AmsLoanPostingUtils";
import {Error} from "@progress/kendo-react-labels";

const carPricePrefix = () => (
  <InputSuffix>
    <Icon name="dollar" />
  </InputSuffix>
);

export interface VehicleEditorDialogProps {
  onClose: () => void;
  onSaveVehicle: (vehicle: Vehicle) => void;
  vehicle: Vehicle;
  canEditInventory?: boolean; // whether the user has the "edit inventory" permission
  canEditFullVehicle?: boolean; // whether the client has the "AllowVehicleFullEdit" feature flag on
}

export default function VehicleEditorDialog(props: VehicleEditorDialogProps) {
  const currentApp = useSelector((state: AppState) => state.applications.editingApp!);
  const lookups = useSelector((state: AppState) => state.applications.lookups);
  const [vehicleToEdit, setVehicleToEdit] = useState<Vehicle>({
    ...props.vehicle,
    locationOfPurchaseId: hasValue(props.vehicle.locationOfPurchaseId) 
      ? props.vehicle.locationOfPurchaseId
      : lookups.vehicleLocationsOfPurchase.find(lop => lop.name === 'UN')?.id
  });
  const bookoutSources = useSelector((state: AppState) => state.applications.lookups.bookoutSources);
  const activeUser: ILoggedInEmployee = useSelector((state: AppState) => state.applications.loggedInUser!.employee);
  const dispatch = useDispatch();
  const amsAcvPermissions = useSelector((state: AppState) => state.applications.amsAcvPermissions);
  // AMS grants are additive: the LOS vehicleACV permission alone shows AND edits (existing behavior);
  // AMS CarSec SeeAcv adds visibility and UpdAcv adds editability for users without the LOS permission.
  const hasLosAcvPermission = userHasPermission(activeUser, permissions.vehicleACV, PermissionSet.R);
  const canViewActualCashValueField = hasLosAcvPermission || amsAcvPermissions?.canSeeAcv === true;
  const canEditActualCashValueField = hasLosAcvPermission || amsAcvPermissions?.canUpdateAcv === true;

  useEffect(() => {
    if (amsAcvPermissions === undefined) {
      fetchData('api/employee/amsAcvPermissions', dispatch)
        .then((result: IAmsAcvPermissions) => dispatch({
          type: SET_AMS_ACV_PERMISSIONS,
          payload: { canSeeAcv: result?.canSeeAcv === true, canUpdateAcv: result?.canUpdateAcv === true }
        }))
        .catch(() => dispatch({ // AMS unreachable: cache deny-all so only the LOS check applies and we don't refetch on every open
          type: SET_AMS_ACV_PERMISSIONS,
          payload: { canSeeAcv: false, canUpdateAcv: false }
        }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const organizations = lookups.organizations;
  const locationsOfPurchaseToDisplay = getAmsLookupsToDisplay(lookups.vehicleLocationsOfPurchase);
  const locationsOfSaleToDisplay = getAmsLocationsOfSaleToDisplay(organizations.filter(o => hasValue(o.amsCode)));
  const vehicleAtOtherLocation = currentApp.organizationId !== vehicleToEdit.organizationId;
  const selectedLocationOfSaleToDisplay = locationsOfSaleToDisplay.find(los => los.id == vehicleToEdit.organizationId);
  const selectedLocationOfSale = lookups.organizations.find(los => los.id == vehicleToEdit.organizationId);
  
  const errors: string[] = [];
  if (!hasValue(selectedLocationOfSale?.amsCode)) {
    errors.push("Select a Location of Sale");
  }
  if (!hasValue(vehicleToEdit.locationOfPurchaseId)) {
    errors.push("Select a Location of Purchase");
  }
  if (!hasValue(vehicleToEdit.carPrice)) {
    errors.push("Enter a price");
  }
  
  return (
    <Dialog
      open={true}
      onClose={() => props.onClose()}
      aria-labelledby="vehicle-dialog-title"
      aria-describedby="vehicle-dialog-description"
      className={styles.vehicleEditorDialogContainer}
      style={{ margin: '0 auto', width: '600px', maxWidth: '600px' }}
    >
      <DialogTitle id="vehicle-dialog-title">Edit Vehicle</DialogTitle>
      <DialogContent>
        {vehicleAtOtherLocation && (
           <DialogContentText id="vehicle-dialog-description" className={styles.locationInfoText}>
            <InfoIcon color='primary' style={{ marginRight: '5px' }} /> This vehicle is in a different location from the application.
          </DialogContentText>
        )
        }
        <div className={props.canEditFullVehicle ? styles.editableInputs : styles.inputs}>
          <div className={styles.rowWrap}>
            <div className={styles.dataColumn}>
              <div className={styles.label}>Stock #</div>
              <div className={styles.value}>{props.vehicle.stockNumber}</div>
            </div>
            {props.canEditFullVehicle !== true && (
              <div className={styles.dataColumn}>
                <div className={styles.label}>Make</div>
                <div className={styles.value}>{capitalizeFirstLetterOfEachWord(props.vehicle.make)}</div>
              </div>
            )}
            {props.canEditFullVehicle === true && (
              <div className={styles.dataColumn}>
                <div className={styles.inputLabel}>Make</div>
                <TextBox
                  fillMode={'outline'}
                  style={{ width: '200px' }}
                  value={vehicleToEdit.make}
                  onChange={(e: TextBoxChangeEvent) => setVehicleToEdit({
                    ...vehicleToEdit,
                    make: e.value as string
                  })}
                />
              </div>
            )}
          </div>
        <div className={styles.rowWrap}>
          {props.canEditFullVehicle !== true && (
            <div className={styles.dataColumn}>
              <div className={styles.label}>Model</div>
              <div className={styles.value}>{capitalizeFirstLetterOfEachWord(props.vehicle.model)}</div>
            </div>
          )}
          {props.canEditFullVehicle === true && (
            <div className={styles.dataColumn}>
              <div className={styles.inputLabel}>Model</div>
              <TextBox
                 fillMode={'outline'}
                style={{ minWidth: '200px' }}
                value={vehicleToEdit.model}
                onChange={(e: TextBoxChangeEvent) => setVehicleToEdit({
                  ...vehicleToEdit,
                  model: e.value as string
                })}
              />
            </div>
          )}
            {props.canEditFullVehicle !== true && (
              <div className={styles.dataColumn}>
                <div className={styles.label}>Year</div>
                <div className={styles.value}>{props.vehicle.year}</div>
              </div>
            )}
            {props.canEditFullVehicle === true && (
              <div className={styles.dataColumn}>
                <div className={styles.inputLabel}>Year*</div>
                <NumericTextBox
                  fillMode={'outline'}
                  // Custom format to remove commas from the year
                  format={'####'}
                  value={vehicleToEdit.year}
                  onChange={(e: NumericTextBoxChangeEvent) => setVehicleToEdit({
                    ...vehicleToEdit,
                    year: e.target.value || vehicleToEdit.year
                  })}
                />
              </div>
            )}
          </div>
          <div className={styles.rowWrap}>
            <div className={styles.dataColumn}>
              <div className={styles.inputLabel}>Type</div>
              <TextBox
                fillMode={'outline'}
                style={{ minWidth: '200px' }}
                value={vehicleToEdit.body}
                onChange={(e: TextBoxChangeEvent) => setVehicleToEdit({
                  ...vehicleToEdit,
                  body: e.value as string
                })}
              />
            </div>
            <div className={styles.dataColumn}>
              <div className={styles.inputLabel}>Location of Sale</div>
              <ComboBox
                style={{ minWidth: '200px' }}
                data={locationsOfSaleToDisplay}
                textField="description"
                fillMode={'outline'}
                value={selectedLocationOfSaleToDisplay}
                onChange={(e) => {
                  setVehicleToEdit({
                    ...vehicleToEdit,
                    organizationId: (e.target.value as AmsLookupForComboBoxDisplay)?.id,
                  })
                }}
              />
            </div>
            <div className={styles.dataColumn}>
              <div className={styles.inputLabel}>Location of Purchase</div>
              <ComboBox
                style={{ minWidth: '200px' }}
                data={locationsOfPurchaseToDisplay}
                textField="description"
                fillMode={'outline'}
                value={locationsOfPurchaseToDisplay.find(los => los.id == vehicleToEdit.locationOfPurchaseId)}
                onChange={(e) => {
                  setVehicleToEdit({
                    ...vehicleToEdit,
                    locationOfPurchaseId: (e.target.value as AmsLookupForComboBoxDisplay)?.id,
                  })
                }}
              />
            </div>
            {props.canEditInventory !== true && (
              <>
                <div className={styles.dataColumn}>
                  <div className={styles.label}>Miles</div>
                  <div className={styles.value}>{numberWithCommas(vehicleToEdit.mileage)}</div>
                </div>
                <div className={styles.dataColumn}>
                  <div className={styles.label}>Price</div>
                  <div className={styles.value}>{vehicleToEdit.carPrice}</div>
                </div>
              </>
            )}
          </div>
        </div>
        {props.canEditInventory === true && (
          <div className={classNames(styles.rowWrap, styles.topMargin)}>
            <NumericTextBox
              label='Price'
              min={0}
              fillMode={'outline'}
              step={100}
              format={'n2'}
              prefix={carPricePrefix}
              style={{ minWidth: '200px' }}
              value={vehicleToEdit.carPrice}
              onChange={(e: NumericTextBoxChangeEvent) => setVehicleToEdit({
                ...vehicleToEdit,
                carPrice: e.target.value || 0
              })}
            />
            <NumericTextBox
              label='Miles'
              min={0}
              step={100}
              fillMode={'outline'}
              style={{minWidth: '200px'}}
              value={vehicleToEdit.mileage}
              onChange={(e: NumericTextBoxChangeEvent) => setVehicleToEdit({
                ...vehicleToEdit,
                mileage: e.target.value || 0
              })}
            />
          </div>
        )}
        <div className={classNames(styles.rowWrap, styles.topMargin)}>
          <NumericTextBox
              label="Bookout"
              step={100}
              min={0}
              fillMode={'outline'}
              style={{ minWidth: '200px' }}
              value={vehicleToEdit?.bookoutValue ?? undefined}
              onChange={(e) => setVehicleToEdit({
                ...vehicleToEdit,
                bookoutValue: e.target.value
              })}
            />
            <ComboBox
              label="Bookout Source"
              style={{ minWidth: '200px' }}
              data={bookoutSources}
              textField="name"
              fillMode={'outline'}
              value={bookoutSources.find(bs => bs.id == vehicleToEdit?.bookoutSourceId)}
              onChange={(e) => {
                setVehicleToEdit({
                  ...vehicleToEdit,
                  bookoutSourceId: (e.target.value as LookupItem)?.id,
                  otherBookoutSource: (e.target.value as LookupItem)?.id?.toLowerCase() !== Constants.bookoutSources.other ? undefined : vehicleToEdit.otherBookoutSource,
                })
              }}
            />
            <Input
              label="Other Bookout Source"
              style={{ minWidth: '200px' }}
              // fillMode={'outline'}
              maxLength={100}
              disabled={vehicleToEdit?.bookoutSourceId?.toLowerCase() !== Constants.bookoutSources.other}
              value={vehicleToEdit?.otherBookoutSource ?? undefined}
              onChange={(e) => setVehicleToEdit({
                ...vehicleToEdit,
                otherBookoutSource: e.target.value as string
              })}
            />
            {canViewActualCashValueField && !vehicleToEdit.isInAms &&
             <NumericTextBox
             label="ACV"
             step={100}
             min={0}
             fillMode={'outline'}
             style={{ minWidth: '200px' }}
             disabled={!canEditActualCashValueField}
             value={vehicleToEdit?.acv}
             onChange={(e) => setVehicleToEdit({
               ...vehicleToEdit,
               acv: e.target.value || 0
             })}
           />
          }
        </div>
        <div style={{display: "flex", flexDirection: "column", marginTop: "10px"}}>
          {errors.map((error, key) => <Error key={key} style={{justifyContent: 'flex-end'}}>{error}</Error>)}
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => props.onClose()}
          fillMode={'outline'}
          autoFocus
        >
          Cancel
        </Button>
        {(
          <Button
            themeColor={'primary'}
            fillMode={'solid'}
            disabled={errors.length > 0}
            onClick={() => {
              props.onSaveVehicle(vehicleToEdit);
            }}
          >
            Update
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
